/**
 * Utility helpers for working with duration values represented in seconds.
 */

export type DurationDisplayUnit = 'ns' | 'µs' | 'ms' | 's' | 'm' | 'h' | 'd';

/**
 * Remove superfluous trailing zeros from a decimal string.
 *
 * Args:
 *   value (string): Decimal string to normalize.
 *
 * Returns:
 *   string: Normalized string without unnecessary trailing zeros.
 *
 * Examples:
 *   >>> trimTrailingZeros('1.2300')
 *   '1.23'
 *   >>> trimTrailingZeros('10.00')
 *   '10'
 */
export const trimTrailingZeros = (value: string): string => {
  return value.replace(/\.0+$/, '').replace(/\.(\d*[1-9])0+$/, '.$1');
};

/**
 * Convert a duration in seconds to a human-readable unit with adaptive precision.
 *
 * Args:
 *   seconds (number): Duration expressed in seconds.
 *
 * Returns:
 *   string: Formatted duration string using the most appropriate unit.
 *
 * Examples:
 *   >>> formatDuration(0.000123)
 *   '123µs'
 *   >>> formatDuration(0.123)
 *   '123ms'
 *   >>> formatDuration(42)
 *   '42s'
 *   >>> formatDuration(125)
 *   '2m5s'
 */
export const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds)) {
    return String(seconds);
  }

  const sign = seconds < 0 ? '-' : '';
  const absSeconds = Math.abs(seconds);

  if (absSeconds === 0) {
    return '0s';
  }

  if (absSeconds < 0.000001) {
    return `${sign}${trimTrailingZeros((absSeconds * 1_000_000_000).toFixed(2))}ns`;
  }

  if (absSeconds < 0.001) {
    return `${sign}${trimTrailingZeros((absSeconds * 1_000_000).toFixed(2))}µs`;
  }

  if (absSeconds < 1) {
    return `${sign}${trimTrailingZeros((absSeconds * 1000).toFixed(2))}ms`;
  }

  if (absSeconds < 60) {
    return `${sign}${trimTrailingZeros(absSeconds.toFixed(2))}s`;
  }

  const total = absSeconds;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.round(total % 60);

  if (h > 0) {
    return `${sign}${h}h${m ? `${m}m` : ''}${s ? `${s}s` : ''}`;
  }

  return `${sign}${m}m${s ? `${s}s` : ''}`;
};

/**
 * Determine a stable display unit for duration values based on the largest magnitude.
 *
 * Args:
 *   maxSeconds (number): Maximum absolute duration value (in seconds) to consider.
 *
 * Returns:
 *   DurationDisplayUnit: Recommended unit for consistently formatting related values.
 *
 * Examples:
 *   >>> selectDurationUnit(0.05)
 *   'ms'
 *   >>> selectDurationUnit(75)
 *   's'
 */
export const selectDurationUnit = (maxSeconds: number): DurationDisplayUnit => {
  if (!isFinite(maxSeconds) || maxSeconds <= 0) {
    return 's';
  }

  if (maxSeconds < 1e-6) {
    return 'ns';
  }
  if (maxSeconds < 1e-3) {
    return 'µs';
  }
  if (maxSeconds < 1) {
    return 'ms';
  }
  if (maxSeconds < 120) {
    return 's';
  }
  if (maxSeconds < 7200) {
    return 'm';
  }
  if (maxSeconds < 172800) {
    return 'h';
  }
  return 'd';
};

/**
 * Format a duration in seconds using a specific unit.
 *
 * Args:
 *   seconds (number): Duration in seconds.
 *   unit (DurationDisplayUnit): Target unit for output.
 *
 * Returns:
 *   string: Duration formatted with the supplied unit suffix.
 *
 * Examples:
 *   >>> formatDurationWithUnit(0.25, 'ms')
 *   '250ms'
 *   >>> formatDurationWithUnit(90, 'm')
 *   '1.5m'
 */
export const formatDurationWithUnit = (
  seconds: number,
  unit: DurationDisplayUnit
): string => {
  if (!isFinite(seconds)) {
    return String(seconds);
  }

  const conversions: Record<DurationDisplayUnit, number> = {
    ns: seconds * 1_000_000_000,
    µs: seconds * 1_000_000,
    ms: seconds * 1_000,
    s: seconds,
    m: seconds / 60,
    h: seconds / 3600,
    d: seconds / 86400,
  };

  const converted = conversions[unit];
  const abs = Math.abs(converted);

  let decimals = 2;
  if (abs >= 100) {
    decimals = 0;
  } else if (abs >= 10) {
    decimals = 1;
  }

  const formatted = trimTrailingZeros(converted.toFixed(decimals));
  return `${formatted}${unit}`;
};

/**
 * Create a stable duration formatter for chart axes based on the provided domain.
 *
 * Args:
 *   domain ([number, number] | undefined): Numeric domain (seconds) for the axis.
 *
 * Returns:
 *   (value: number) => string: Formatter that keeps the same unit across all ticks.
 *
 * Examples:
 *   >>> const formatter = createDurationTickFormatter([0, 0.2])
 *   >>> formatter(0.05)
 *   '50ms'
 */
export const createDurationTickFormatter = (
  domain?: [number, number]
): ((value: number) => string) => {
  const maxSeconds = domain
    ? Math.max(Math.abs(domain[0]), Math.abs(domain[1]))
    : undefined;
  const unit = selectDurationUnit(maxSeconds ?? 1);

  return (value: number) => formatDurationWithUnit(value, unit);
};
